import { Signal } from "~/sdk/signal";
import { useStore } from "@tanstack/react-store";
import type { ReactNode } from "react";
import { lazy, Suspense } from "react";
import type { Brand } from "~/components/Header/Brands";
import MobileMenuAccountOptions from "~/components/Header/MobileMenuAccountOptions";
import type { Props as MenuProps } from "~/components/Header/MobileNavMenu";
import Cart from "~/components/minicart/Cart";
import ShareCartButton from "~/components/minicart/common/ShareCartButton";
import type { Props as SearchbarProps } from "~/components/search/Searchbar";
import Button from "~/components/ui/Button";
import Drawer from "~/components/ui/Drawer";
import Icon from "~/components/ui/Icon";
import Brands from "~/components/Header/Brands";

import { useUI } from "~/sdk/useUI";
const MobileNavMenu = lazy(
  () => import("~/components/Header/MobileNavMenu"),
);
export interface Props {
  menu: MenuProps;
  brands?: Brand[];
  searchbar?: SearchbarProps;
  /**
   * @ignore_gen true
   */
  children?: ReactNode;
  freeShippingTarget?: number;}
const MenuAside = ({
  onClose,
  children,
  displayBrandsSignal,
  brands,
}: {
  onClose: () => void;
  children: ReactNode;
  displayBrandsSignal: Signal<boolean>;
  brands?: Brand[];
}) => {
  const displayBrands = useStore(displayBrandsSignal.store, (s) => s);
  return (
  <div className="flex flex-col h-full divide-y max-w-[100vw] bg-[#F0F0F0] w-full">
    <div className="flex items-center justify-between">
      {brands && <Brands brands={brands} device="MOBILE" />}
      <Button
        className={`text-[#000] flex mr-[18px]`}
        onClick={onClose}
        aria-label="Fechar menu"
      >
        <Icon id="XMark" size={20} strokeWidth={2} />
      </Button>
    </div>
    <Suspense
      fallback={
        <div className="w-screen flex items-center justify-center">
          <span className="loading loading-ring" />
        </div>
      }
    >
      {displayBrands && brands
        ? (
          <div className="flex flex-col justify-between">
            {brands && <Brands brands={brands} device="MOBILE" />}
            <MobileMenuAccountOptions />
          </div>
        )
        : children}
    </Suspense>
  </div>
  );
};
const CartAside = ({
  onClose,
  children,
}: {
  onClose: () => void;
  children: ReactNode;
}) => (
  <div className="bg-base-100 grid font-['Poppins'] grid-rows-[auto_1fr] h-full divide-y max-w-[100vw]">
    <div className="flex justify-between items-center px-4 py-3">
      <span className="font-medium text-2xl">Carrinho</span>
      <div className="flex items-center gap-1">
        <ShareCartButton />
        <Button
          aria-label="Fechar carrinho"
          className="btn btn-ghost btn-sm"
          onClick={onClose}
        >
          <Icon id="XMark" size={20} strokeWidth={2} />
        </Button>
      </div>
    </div>
    <Suspense
      fallback={
        <div className="w-screen flex items-center justify-center">
          <span className="loading loading-ring" />
        </div>
      }
    >
      {children}
    </Suspense>
  </div>
);
function Drawers({
  menu,
  brands,
  children,
  freeShippingTarget,
}: Props) {
  const { displayCart, displayMenu, displayBrandsAtMobileMenuDrawer } = useUI();
  const isMenuOpen = useStore(displayMenu.store, (s) => s);
  const isCartOpen = useStore(displayCart.store, (s) => s);
  return (
    <Drawer // menu drawer
      open={isMenuOpen}
      onClose={() => {
        displayMenu.value = false;
      }}
      aside={
        <MenuAside
          onClose={() => {
            displayMenu.value = false;
          }}
          displayBrandsSignal={displayBrandsAtMobileMenuDrawer}
          brands={brands}
        >
          {isMenuOpen && <MobileNavMenu {...menu} />}
        </MenuAside>
      }
    >
      <Drawer // cart drawer
        className="drawer-end"
        open={isCartOpen !== false}
        onClose={() => (displayCart.value = false)}
        aside={
          <CartAside onClose={() => (displayCart.value = false)}>
            <Cart freeShippingTarget={freeShippingTarget} />
          </CartAside>
        }
      >
        {children}
      </Drawer>
    </Drawer>
  );
}
export default Drawers;
